import { Injectable } from '@angular/core';
import { DeviceInfo } from 'src/app/models/models';
import { UserModelRefDisplayName, UserService } from 'src/app/shared-lib/domains/user';
import { AppService } from 'src/app/shared/app.service';
import { EncryptionService } from 'src/app/shared/encryption.service';
import { createAcceptance } from '../shared-lib/domains/acceptance/acceptance.helpers';
import { Acceptance, AcceptanceExtended } from '../shared-lib/domains/acceptance/acceptance.models';
import { AcceptanceService } from '../shared-lib/domains/acceptance/acceptance.service';

@Injectable({ providedIn: 'root' })
export class AcceptanceFlows {
  constructor(
    private acceptanceService: AcceptanceService,
    private userService: UserService,
    private appService: AppService,
    private encryptS: EncryptionService
  ) { }

  async createAcceptanceEntry(acceptanceBase: Acceptance, user?: any): Promise<string> {
    const payload = await this.buildAcceptancePayload(acceptanceBase, user);
    return this.acceptanceService.upsertAcceptanceRecord(payload);
  }

  async insertAcceptance(acceptanceBase: Acceptance, user?: any): Promise<string> {
    return this.createAcceptanceEntry(acceptanceBase, user);
  }

  /**
   * Create a piggy-transactions acceptance and mark the user as referrer-accepted.
   */
  async referrerAcceptance(userId: string): Promise<string> {
    const acceptance = createAcceptance('referrer');
    const acceptanceId = await this.insertAcceptance(acceptance);
    if (acceptanceId) {
      this.userService.setUser({
        ...this.userService.userG,
        id: userId,
        acceptance: {
          ...this.userService.userG.acceptance,
          referrer: true
        }
      });
    }
    return acceptanceId;
  }

  async userAcceptance(user: any) {
    const acceptance = createAcceptance('user', user.id);
    const acceptanceId = await this.createAcceptanceEntry(acceptance, user);
    return acceptanceId;
  }

  async changeAdsSubscription(user: any) {
    const acceptance = createAcceptance('user', user.id, { ads: !user.acceptance.ads });

    const acceptanceId = await this.createAcceptanceEntry(acceptance);
    if (acceptanceId) {
      await this.userService.setUser({
        ...user,
        acceptance: {
          ...user.acceptance,
          privacy: true,
          tos: true,
          ads: !user.acceptance.ads
        }
      });
    }
    return acceptanceId;
  }

  getPrintAllowed() {
    try {
      const printAllowed = this.encryptS.decrypt(localStorage.getItem('printAllowed'));
      return printAllowed === 'true' ? true : printAllowed === 'false' ? false : null;
    } catch {
      localStorage.removeItem('printAllowed');
      return null;
    }
  }

  private async buildAcceptancePayload(acceptanceBase: Acceptance, _user?: any): Promise<AcceptanceExtended> {
    const userTemp = _user ?? this.userService.userG;
    const user = userTemp?.id ?
      { id: userTemp.id, displayName: userTemp.displayName } as UserModelRefDisplayName :
      null;
    const platform = await this.getDeviceInfo();
    const ipInfo = await this.appService.getIpInfo();
    return {
      ...acceptanceBase,
      user,
      platform,
      ipInfo
    } as AcceptanceExtended;
  }

  private async getDeviceInfo(): Promise<DeviceInfo> {
    const deviceInfo: DeviceInfo = {
      brands: 'Unknown',
      mobile: false,
      platform: navigator.platform || 'Unknown',
      userAgent: navigator.userAgent || 'Unknown',
      screenResolution: `${screen.width}x${screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      cookiesEnabled: navigator.cookieEnabled || false,
    };

    try {
      if ('userAgentData' in navigator) {
        const uaData = navigator.userAgentData as any;
        deviceInfo.brands = uaData.brands
          ? uaData.brands.map((brand: any) => `${brand.brand} ${brand.version}`).join(', ')
          : 'Unknown';
        deviceInfo.mobile = uaData.mobile || false;
      } else {
        deviceInfo.mobile = /Mobi|Android/i.test(navigator.userAgent);
      }

      if ('connection' in navigator) {
        const connection = navigator.connection as any;
        deviceInfo.connectionType = connection.effectiveType || 'Unknown';
        deviceInfo.bandwidth = connection.downlink || 'Unknown';
      }
    } catch (error) {
      console.error('Error al obtener información del dispositivo:', error);
    }

    return deviceInfo;
  }
}
